// Cancel Licenses Modal
// ----------------------
// Confirms cancelling some or all of the licenses in one purchase.
//
// Two things this screen has to get right, because both are irreversible in
// practice and cost the customer money:
//
//   1. WHICH licenses go. Spare (unattached) licenses are used up first — they
//      cost the customer nothing to lose. Only when the count exceeds the spares
//      does an app have to lose cover, and then the owner picks which app rather
//      than the backend guessing. That choice is what the pendingCancelAt marker
//      carries through to the reconcile at the period boundary.
//
//   2. WHAT it means. Access continues to the end of the paid period (there is
//      no refund of the remainder), and if this is the last live license the
//      account closes on that date. Those are different consequences and get
//      different confirmations.

import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LicenseSubscriptionGroup } from "@/services/billingSubscriptions.service";
import { TenantEntitlement } from "@/services/tenantEntitlements.service";
import { formatShortDate } from "@/utils/date";

interface CancelLicensesModalProps {
  group: LicenseSubscriptionGroup;
  planLabel: string;
  // Every license in the tenant, used to resolve this group's ids to apps.
  entitlements: TenantEntitlement[];
  appNameById: Map<string, string>;
  // True when cancelling EVERY license in this group would leave the tenant
  // with nothing live. Computed by the caller from all groups; the backend
  // re-derives it authoritatively and returns it on the result.
  wouldCloseAccountIfAllCancelled: boolean;
  // Days data is retained after the account closes. Sourced from the backend so
  // it cannot drift from the configured grace period.
  dataRetentionDays: number | null;
  isSubmitting: boolean;
  errorMessage: string | null;
  onCancelLicenses: (entitlementIds: string[]) => void;
  onClose: () => void;
}

export default function CancelLicensesModal({
  group,
  planLabel,
  entitlements,
  appNameById,
  wouldCloseAccountIfAllCancelled,
  dataRetentionDays,
  isSubmitting,
  errorMessage,
  onCancelLicenses,
  onClose,
}: CancelLicensesModalProps) {
  // Split this group's licenses into spares and app-covering ones. Spares are
  // spent first because losing one costs the customer nothing.
  const { spareIds, boundLicenses } = useMemo(() => {
    const byId = new Map(entitlements.map((entitlement) => [entitlement.id, entitlement]));
    const spares: string[] = [];
    const bound: Array<{ id: string; appId: string; appName: string }> = [];
    for (const entitlementId of group.entitlementIds || []) {
      const entitlement = byId.get(entitlementId);
      if (!entitlement) continue;
      if (entitlement.boundWebAppId) {
        bound.push({
          id: entitlementId,
          appId: entitlement.boundWebAppId,
          appName: appNameById.get(entitlement.boundWebAppId) || "Assigned app",
        });
      } else {
        spares.push(entitlementId);
      }
    }
    bound.sort((first, second) => first.appName.localeCompare(second.appName));
    return { spareIds: spares, boundLicenses: bound };
  }, [group.entitlementIds, entitlements, appNameById]);

  const maxCancellable = spareIds.length + boundLicenses.length;
  const [quantity, setQuantity] = useState(1);
  // Which APPS lose cover. Only meaningful once the count exceeds the spares.
  const [selectedBoundIds, setSelectedBoundIds] = useState<string[]>([]);

  const appsToChoose = Math.max(0, quantity - spareIds.length);

  // Keep the app selection consistent with the count: top up from the start of
  // the list when more are needed, trim from the end when fewer are. Doing this
  // here (rather than only on click) means the confirm button's enabled state
  // always matches what the summary says will happen.
  useEffect(() => {
    setSelectedBoundIds((previous) => {
      if (previous.length === appsToChoose) return previous;
      if (previous.length > appsToChoose) return previous.slice(0, appsToChoose);
      const chosen = new Set(previous);
      const topUp = [...previous];
      for (const license of boundLicenses) {
        if (topUp.length >= appsToChoose) break;
        if (!chosen.has(license.id)) {
          topUp.push(license.id);
          chosen.add(license.id);
        }
      }
      return topUp;
    });
  }, [appsToChoose, boundLicenses]);

  const toggleBoundLicense = (licenseId: string) => {
    setSelectedBoundIds((previous) => {
      if (previous.includes(licenseId)) {
        return previous.filter((id) => id !== licenseId);
      }
      // Keep the newest choice and drop the oldest, so clicking a third app
      // when only two may go swaps rather than silently doing nothing.
      const next = [...previous, licenseId];
      return next.slice(Math.max(0, next.length - appsToChoose));
    });
  };

  const selectionComplete = selectedBoundIds.length === appsToChoose;
  const cancellingEverything = quantity >= maxCancellable;
  const closesAccount = cancellingEverything && wouldCloseAccountIfAllCancelled;
  const endsOn = group.currentPeriodEnd ? formatShortDate(group.currentPeriodEnd) : null;

  const entitlementIdsToCancel = [
    ...spareIds.slice(0, Math.min(quantity, spareIds.length)),
    ...selectedBoundIds,
  ];

  const setQuantitySafely = (value: number) => {
    setQuantity(Math.max(1, Math.min(maxCancellable, value)));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-licenses-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 p-5">
          <div>
            <h2 id="cancel-licenses-title" className="text-base font-semibold text-gray-900">
              Cancel licenses
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {planLabel} · {maxCancellable} license{maxCancellable === 1 ? "" : "s"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="flex items-center justify-between gap-4">
            <label className="text-sm text-gray-700" htmlFor="cancel-quantity">
              How many licenses do you want to cancel?
            </label>
            <div
              className="flex items-center rounded-md border border-gray-200"
              role="group"
              aria-label="Number of licenses to cancel"
            >
              <button
                type="button"
                onClick={() => setQuantitySafely(quantity - 1)}
                disabled={quantity <= 1}
                aria-label="Decrease"
                className="px-2 py-1 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span
                id="cancel-quantity"
                aria-live="polite"
                className="min-w-[2rem] px-1 text-center text-sm tabular-nums text-gray-900"
              >
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantitySafely(quantity + 1)}
                disabled={quantity >= maxCancellable}
                aria-label="Increase"
                className="px-2 py-1 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Spares absorb the first cancellations, so say so — it tells the
              owner why no app is affected yet. */}
          {spareIds.length > 0 && (
            <p className="text-xs text-gray-500">
              {Math.min(quantity, spareIds.length)} of {spareIds.length} unassigned
              license{spareIds.length === 1 ? "" : "s"} will be used first, so no app
              loses cover until you go past {spareIds.length}.
            </p>
          )}

          {appsToChoose > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-gray-900">
                Choose {appsToChoose} app{appsToChoose === 1 ? "" : "s"} to stop covering
              </p>
              <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {boundLicenses.map((license) => {
                  const isSelected = selectedBoundIds.includes(license.id);
                  return (
                    <label
                      key={license.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleBoundLicense(license.id)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                        {license.appName}
                      </span>
                      {isSelected && (
                        <span className="shrink-0 text-xs font-medium text-amber-600">
                          Loses cover
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                A pentest can't run on an app without a license. You can attach another
                license to it later from this page.
              </p>
            </div>
          )}

          {/* What actually happens, stated plainly. */}
          <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
            <p>
              {endsOn ? (
                <>
                  Everything keeps working until{" "}
                  <span className="font-medium text-gray-900">{endsOn}</span>, the end
                  of the period you've already paid for. The change takes effect then.
                </>
              ) : (
                <>
                  Everything keeps working until the end of the period you've already
                  paid for. The change takes effect then.
                </>
              )}
            </p>
            <p className="mt-1.5">
              The remainder of the period isn't refunded. You can undo this from this
              page any time before it takes effect.
            </p>
          </div>

          {closesAccount && (
            <div
              className="rounded-md border border-red-200 bg-red-50 p-3"
              role="alert"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div className="text-xs text-red-800">
                  <p className="font-semibold">
                    This is your last license — your account will close
                    {endsOn ? ` on ${endsOn}` : ""}.
                  </p>
                  <p className="mt-1">
                    Sign-in is disabled from that date and pentests stop. Your reports
                    and data are kept for{" "}
                    {dataRetentionDays ? `${dataRetentionDays} days` : "a grace period"}{" "}
                    afterwards, then permanently deleted. Download anything you need
                    before then.
                  </p>
                  <p className="mt-1">
                    You can undo this any time before {endsOn || "it takes effect"}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {errorMessage && (
            <p className="text-sm text-red-600" role="alert">
              {errorMessage}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Keep licenses
          </Button>
          <Button
            size="sm"
            variant={closesAccount ? "destructive" : "default"}
            disabled={isSubmitting || !selectionComplete}
            onClick={() => onCancelLicenses(entitlementIdsToCancel)}
            className="gap-1.5"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
            ) : closesAccount ? (
              "Cancel and close account"
            ) : (
              `Cancel ${quantity} license${quantity === 1 ? "" : "s"}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
